import { useEffect, useState } from 'react';

import { MEDIUM_ICON_HEIGHT, MEDIUM_ICON_WIDTH } from 'modules/experiments/constants';
import { useExperimentsTranslations } from 'modules/experiments/hooks';
import { ExperimentsTableItem } from 'modules/experiments/types';
import {
  getNonControlVariationCount,
  sortControlVariantFirst,
  isExpandedDataMatched,
} from 'modules/experiments/utils';
import { useTableFacetContext } from 'modules/experiments/contexts';

import { getMediumIcon } from 'utils/messageMediums';
import { getCampaignEditorLink } from 'utils/appRoutes';
import { Link } from 'ui/links';

import { TAG_SIZES } from 'ui/tags';
import { CollapsibleContentArrow, ARROW_DIRECTION_DOWN } from 'ui/collapsibleContent';
import { MemoSearchableText } from 'ui-beta/searchableText';

import { ROW_CLICK_ACTIONS, useRumAction } from 'services/observability/datadog';

import {
  WordBreakLabel,
  NameCellWrapper,
  NameInfoWrapper,
  ArrowButton,
  VariationCountLabel,
  NameCellInfoContent,
  StyledNameLink,
  StyledTagGroup,
} from './Styled';

import { ExpandedNameCellContent } from './ExpandedNameCellContent';

type NameCellProps = {
  experiment: ExperimentsTableItem;
  experimentLinkAttributes: React.AnchorHTMLAttributes<HTMLAnchorElement>;
};

export const NameCell = ({ experiment, experimentLinkAttributes }: NameCellProps) => {
  const { t } = useExperimentsTranslations();
  const addSignal = useRumAction(ROW_CLICK_ACTIONS);
  const { name, id, campaign, evenlySplitVariations, type, variations = [] } = experiment;
  const [isCollapsed, setIsCollapsed] = useState(true);
  const Icon = getMediumIcon(campaign.messageMedium);
  const variationsWithControlFirst = sortControlVariantFirst(variations);
  const nonControlVariationCount = getNonControlVariationCount(variations);

  // Search Highlighting
  const { search: searchedText } = useTableFacetContext();

  useEffect(() => {
    if (!searchedText.length) return;
    const hasMatch = isExpandedDataMatched(id, campaign.id, searchedText);
    if (hasMatch) setIsCollapsed(false);
  }, [id, campaign.id, searchedText]);

  return (
    <>
      <NameCellWrapper data-test="name-cell-wrapper">
        <NameCellInfoContent>
          <StyledNameLink {...experimentLinkAttributes}>
            <WordBreakLabel>
              <MemoSearchableText
                isCaseSensitive={false}
                text={name}
                searchText={searchedText || ''}
              />
            </WordBreakLabel>
          </StyledNameLink>
          <NameInfoWrapper>
            <Icon width={MEDIUM_ICON_WIDTH} height={MEDIUM_ICON_HEIGHT} />
            <span>{t(`campaignTypes.${campaign.type}`)}</span>
            <span>•</span>
            <Link size="caption" inTable href={getCampaignEditorLink(campaign.id, campaign.state)}>
              <WordBreakLabel>
                <MemoSearchableText
                  isCaseSensitive={false}
                  text={campaign.name}
                  searchText={searchedText || ''}
                />
              </WordBreakLabel>
            </Link>
            {!!campaign.labels?.length && (
              <StyledTagGroup
                value={campaign.labels?.map((label) => label.name)}
                tagSize={TAG_SIZES.SMALL}
                maxTags={2}
              />
            )}
          </NameInfoWrapper>
        </NameCellInfoContent>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {!!nonControlVariationCount && (
            <VariationCountLabel>
              {t('table.nameCell.variantCount', { count: nonControlVariationCount })}
            </VariationCountLabel>
          )}
          <ArrowButton
            onClick={() => {
              addSignal('INDEX_ROW_TOGGLED');
              setIsCollapsed((collapsedState) => !collapsedState);
            }}
          >
            <CollapsibleContentArrow
              isCollapsed={isCollapsed}
              arrowStart={ARROW_DIRECTION_DOWN}
              arrowEnd={180}
              width={16}
              height={16}
            />
          </ArrowButton>
        </div>
      </NameCellWrapper>
      {isCollapsed ? null : (
        <ExpandedNameCellContent
          id={id}
          campaignId={campaign.id}
          messageMedium={campaign.messageMedium}
          searchedText={searchedText}
          evenlySplitVariations={evenlySplitVariations}
          sendTimeUseRecipientTimeZones={campaign.sendTimeUseRecipientTimeZones}
          variationsWithControlFirst={variationsWithControlFirst}
          type={type}
        />
      )}
    </>
  );
};
